import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import { canUseCatalogAction } from '../api/formal/phase1Permissions';
import { createSettingsApi } from '../settings-integration/settingsApi';
import { createEquipmentApi } from './equipmentApi';

export type EquipmentPageRuntime = {
  api: ReturnType<typeof createEquipmentApi>;
  fieldApi?: ReturnType<typeof createSettingsApi>;
  canManageDefinitions?: boolean;
  canRead: boolean;
  canManage: boolean;
  canCreate?: boolean;
  canSetStatus?: boolean;
  /** 阶段一：删除只允许管理员部门，服务端仍会再次校验。 */
  canDelete: boolean;
  recoverUnauthorized(): Promise<void>;
};

const EquipmentPageRuntimeContext = createContext<EquipmentPageRuntime | null>(
  null,
);

export function EquipmentPageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: EquipmentPageRuntime;
}) {
  return (
    <EquipmentPageRuntimeContext.Provider value={value}>
      {children}
    </EquipmentPageRuntimeContext.Provider>
  );
}

export function ActiveEquipmentPageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, transport, refresh } = useActiveSession();
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo<EquipmentPageRuntime>(
    () => ({
      api: createEquipmentApi(transport, ready?.csrfToken ?? null),
      fieldApi: createSettingsApi(transport, ready?.csrfToken ?? null),
      canManageDefinitions:
        ready?.user.permissions.includes('quote.field_definition.manage') ??
        false,
      canRead: canUseCatalogAction(ready?.user, 'equipment', 'read'),
      canManage: canUseCatalogAction(ready?.user, 'equipment', 'edit'),
      canCreate: canUseCatalogAction(ready?.user, 'equipment', 'create'),
      canSetStatus: canUseCatalogAction(ready?.user, 'equipment', 'set_status'),
      canDelete: canUseCatalogAction(ready?.user, 'equipment', 'delete'),
      recoverUnauthorized: refresh,
    }),
    [ready, refresh, transport],
  );
  if (!ready) return null;
  return (
    <EquipmentPageRuntimeProvider value={value}>
      {children}
    </EquipmentPageRuntimeProvider>
  );
}

export function useEquipmentPageRuntime(): EquipmentPageRuntime {
  const value = useContext(EquipmentPageRuntimeContext);
  if (!value) throw new Error('EquipmentPageRuntimeProvider is required');
  return value;
}
